import {
  FileCheck,
  Building,
  Briefcase,
  Shield,
  Globe2,
  Factory,
  Store,
  Utensils,
  Pill,
  Truck,
  Home,
  Scale,
  CheckCircle,
  Award,
  FileText,
  ClipboardCheck,
  BookOpen,
  Zap
} from "lucide-react";

export default function RegistrationLicensing() {
  const coreServices = [
    {
      icon: FileCheck,
      title: "License Identification & Advisory",
      description: "Strategic guidance on required licenses and permits",
      points: [
        "Comprehensive business activity assessment",
        "Industry-specific license requirement mapping",
        "Central, state, and local license identification",
        "Compliance framework design and timeline planning"
      ]
    },
    {
      icon: ClipboardCheck,
      title: "Application & Documentation Support",
      description: "End-to-end license application assistance",
      points: [
        "Document preparation and verification",
        "Form filing and application submission",
        "Liaison with regulatory authorities",
        "Query resolution and clarification support"
      ]
    },
    {
      icon: Award,
      title: "Renewals & Ongoing Compliance",
      description: "Continuous license management and maintenance",
      points: [
        "License renewal tracking and reminders",
        "Annual compliance filings and submissions",
        "Modification and amendment support",
        "Regulatory audit readiness assistance"
      ]
    }
  ];

  const licenseCategories = [
    {
      icon: Building,
      title: "Business & Trade Licenses",
      color: "bg-blue-500",
      licenses: [
        "Shops and Establishment License",
        "Trade License / Municipal License",
        "Professional Tax Registration",
        "Udyog Aadhaar / MSME Registration",
        "Udyam Registration (MSME)",
        "Start-up India Recognition"
      ],
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Factory,
      title: "Manufacturing & Industrial Licenses",
      color: "bg-indigo-500",
      licenses: [
        "Factory License (under Factories Act)",
        "Pollution Control Board (PCB) Consent",
        "Environmental Clearance (EC)",
        "Explosives License",
        "Boiler License",
        "Industrial Park / SEZ Approvals"
      ],
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Utensils,
      title: "Food & Hospitality Licenses",
      color: "bg-green-500",
      licenses: [
        "FSSAI Food License (Basic, State, Central)",
        "Eating House License",
        "Hotel & Restaurant License",
        "Liquor License / Bar License",
        "Health Trade License",
        "Fire Safety NOC for restaurants"
      ],
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Pill,
      title: "Healthcare & Pharmaceutical Licenses",
      color: "bg-red-500",
      licenses: [
        "Drug License (Retail / Wholesale / Manufacturing)",
        "CDSCO Approvals (Central Drugs Standard Control)",
        "Medical Device License",
        "Clinical Establishment Registration",
        "Bio-Medical Waste Authorization",
        "Narcotic & Psychotropic Substance License"
      ],
      image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Truck,
      title: "Import-Export & Trade Licenses",
      color: "bg-yellow-600",
      licenses: [
        "Import Export Code (IEC) Registration",
        "IEC Modification / Amendment",
        "DGFT (Directorate General of Foreign Trade) Licenses",
        "Customs Broker License",
        "Export House Certificate",
        "APEDA / EIA Registration (agricultural exports)"
      ],
      image: "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Home,
      title: "Real Estate & Construction Licenses",
      color: "bg-purple-500",
      licenses: [
        "RERA Registration (Real Estate Regulatory Authority)",
        "Building Plan Approval",
        "Occupancy Certificate (OC)",
        "Construction License",
        "Architect / Engineer Registration",
        "Labor License for construction sites"
      ],
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Store,
      title: "Retail & E-commerce Licenses",
      color: "bg-pink-500",
      licenses: [
        "GST Registration (for online sellers)",
        "Marketplace Seller Registration (Amazon, Flipkart)",
        "Legal Metrology Registration (weights & measures)",
        "Barcode Registration (GS1 India)",
        "BIS Certification (Bureau of Indian Standards)",
        "E-commerce Platform Registration"
      ],
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80&auto=format&fit=crop"
    },
    {
      icon: Scale,
      title: "Financial & Professional Services Licenses",
      color: "bg-cyan-500",
      licenses: [
        "NBFC Registration (Non-Banking Financial Company)",
        "SEBI Registration (Investment Advisors / Brokers)",
        "IRDAI License (Insurance)",
        "Payment Gateway / Aggregator License (RBI)",
        "Chartered Accountant / Lawyer Registration",
        "Money Changer License"
      ],
      image: "https://images.unsplash.com/photo-1554224311-beee460c201e?w=800&q=80&auto=format&fit=crop"
    }
  ];

  const certifications = [
    {
      icon: Award,
      name: "ISO Certifications",
      items: ["ISO 9001 (Quality)", "ISO 14001 (Environment)", "ISO 27001 (Information Security)", "ISO 45001 (Health & Safety)"]
    },
    {
      icon: Shield,
      name: "Safety & Compliance",
      items: ["Fire Safety NOC", "Fire Department License", "Labour License", "Contract Labour License"]
    },
    {
      icon: Zap,
      name: "Utility & Infrastructure",
      items: ["Electricity Connection (Commercial/Industrial)", "Water Connection NOC", "Sewage Treatment Approval"]
    },
    {
      icon: BookOpen,
      name: "Educational & Training",
      items: ["Educational Institution License", "Training Center Registration", "NSDC Affiliation", "University Affiliation"]
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Initial Consultation",
      description: "Assess business activities and identify required licenses"
    },
    {
      step: "2",
      title: "Documentation",
      description: "Prepare and verify all required documents and applications"
    },
    {
      step: "3",
      title: "Filing & Submission",
      description: "Submit applications to relevant authorities with proper follow-up"
    },
    {
      step: "4",
      title: "Approval & Delivery",
      description: "Obtain licenses and ensure compliance framework setup"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Registration & Licensing Advisory
            </h1>
            <p className="text-xl md:text-2xl text-yellow-50 mb-4">
              Comprehensive Licensing Solutions for Every Industry
            </p>
            <p className="text-yellow-100 text-lg">
              Expert guidance on obtaining, renewing, and maintaining all business licenses and regulatory permits across India [web:40][web:44][web:45]
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4 text-black"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Our Licensing Advisory Services
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {coreServices.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:border-yellow-500 transition-all duration-300 hover:shadow-xl"
              >
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <service.icon className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-center text-black">
                  {service.title}
                </h3>
                <p className="text-yellow-600 text-sm text-center mb-4 font-semibold">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-2 inline-block w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* License Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4 text-black"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Industry-Specific Licenses We Handle
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive licensing support across all major industries and business verticals [web:45][web:46][web:47]
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {licenseCategories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-6 flex items-center gap-3">
                    <div className={`p-3 ${category.color} rounded-lg`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{category.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-2">
                    {category.licenses.map((license, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm leading-relaxed">{license}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Special Licenses */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4 text-black"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Certifications & Special Permits
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200 hover:border-yellow-500 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <cert.icon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-bold text-black">{cert.name}</h3>
                </div>
                <ul className="space-y-2">
                  {cert.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-yellow-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4 text-black"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Our Licensing Process
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {processSteps.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-bold mb-2 text-black">{process.title}</h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4 text-black"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Why Choose Our Licensing Services?
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 text-center border-t-4 border-yellow-500 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">500+</h3>
              <p className="text-gray-600">Licenses Processed</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 text-center border-t-4 border-yellow-500 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe2 className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">Pan-India</h3>
              <p className="text-gray-600">Service Coverage</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 text-center border-t-4 border-yellow-500 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">Expert Team</h3>
              <p className="text-gray-600">CA, CS & Legal Experts</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 text-center border-t-4 border-yellow-500 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">98%</h3>
              <p className="text-gray-600">Success Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Help with Business Licensing?
          </h2>
          <p className="text-lg text-yellow-50 mb-8">
            Get expert assistance in obtaining all required licenses and permits for your business with full compliance support [web:44][web:47]
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-8 py-4 bg-white text-yellow-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              Get Started
            </a>
            <a
              href="tel:+919217468906"
              className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-yellow-600 transition-all duration-300"
            >
              Call +91 92174 68906
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <p className="text-sm text-gray-400">
            All licensing and registration services are subject to applicable laws and regulations. Final approvals remain at the discretion of respective regulatory authorities [web:44][web:45]
          </p>
        </div>
      </section>
    </div>
  );
}
